// MobileMenu.jsx

import React, { useCallback, useEffect, useMemo, useState } from 'react';

// Context
import { useNavigation } from 'context/NavigationContext';
import { useDatabase } from 'context/DatabaseContext';
import { useTheme } from 'context/ThemeContext';
import { useUser } from 'context/UserContext';
import { useNodeTypes } from 'context/NodeTypesContext';
import { useWorkspace } from 'context/WorkspaceContext';
import { useCurrentLanguage } from 'context/LanguageContext';

// Services
import {
  isWorkspaceBillingInGracePeriod,
  isWorkspaceBillingNavigationLocked,
  subscribeToWorkspaceBillingRecord,
} from 'services/billing/WorkspaceBillingService';

// Routing
import { useLocation, useNavigate } from 'react-router-dom';

// Utilities
import { SectionType } from 'utilities/Enums';
import { getSectionPath, inferSectionFromPath } from 'utilities/Routing';

// Hooks
import useTopLevelNodeCreate from 'hooks/useTopLevelNodeCreate';
import useWorkspaceAccess from 'hooks/useWorkspaceAccess';

// CSS
import './MobileMenu.css';

// Icons

// Components
import AddModal from 'components/addmodal/AddModal';
import MobileMenuAddButton from './addbutton/MobileMenuAddButton';
import MobileMenuButton from './button/MobileMenuButton';
import CameraModal from 'editor/nodeadd/camera/CameraModal';

function formatBillingLockNotice(record) {
  if (!record) return '';

  const gracePeriodEndsAt = record?.gracePeriodEndsAt instanceof Date
    ? record.gracePeriodEndsAt
    : null;

  if (isWorkspaceBillingNavigationLocked(record)) {
    return 'Billing access is locked. Open workspace settings to update payment.';
  }

  if (!isWorkspaceBillingInGracePeriod(record)) {
    return '';
  }

  if (!gracePeriodEndsAt) {
    return 'Billing issue detected. Update payment to avoid lockout.';
  }

  return `Billing issue detected. Update payment before ${gracePeriodEndsAt.toLocaleString()}.`;
}

/**
 * MobileMenu
 * 
 * Renders the mobile menu with navigation buttons and add node functionality.
 * 
 * @returns {ReactNode} MobileMenu component.
 */
const MobileMenu = () => {

  // ----- CONTEXT ----- //

  // Global Context
  const {
    setCurrentNode,
    setCurrentSection,
  } = useNavigation();

  const { setActiveDatabase } = useDatabase();

  // User Context
  const {
    user,
    userNotifications,
    userSettings,
  } = useUser();

  // Theme Context
  const { theme } = useTheme();

  const { getType } = useNodeTypes();
  const { activeWorkspace } = useWorkspace();
  const { can } = useWorkspaceAccess(activeWorkspace);
  const language = useCurrentLanguage();

  // Routing
  const navigate = useNavigate();
  const location = useLocation();

  // ----- STATE ----- //

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [billingRecord, setBillingRecord] = useState(null);
  const canReadWorkspaceBilling = can('workspace.update');
  const {
    cameraOpen,
    cameraContentType,
    uploadInputRef,
    pendingUploadAccept,
    pendingUploadMultiple,
    handleTopLevelActionSelect,
    handleUploadFileSelect,
    setCameraOpen,
    handleCameraContentCreated,
  } = useTopLevelNodeCreate({
    user,
    activeWorkspaceKey: activeWorkspace?.key || null,
    getType,
    language,
    navigate,
    setCurrentNode,
    setCurrentSection,
    setActiveDatabase,
  });

  // ----- EFFECTS ----- //

  useEffect(() => {
    if (!canReadWorkspaceBilling) {
      setBillingRecord(null);
      return () => {};
    }

    return subscribeToWorkspaceBillingRecord(activeWorkspace?.key, setBillingRecord);
  }, [activeWorkspace?.key, canReadWorkspaceBilling]);

  // ----- MEMOIZED VALUES ----- //

  const activeWorkspaceKey = useMemo(() => {
    const key = activeWorkspace?.key || activeWorkspace?.id || userSettings?.ACTIVE_WORKSPACE_KEY || '';
    return String(key).trim();
  }, [activeWorkspace?.id, activeWorkspace?.key, userSettings?.ACTIVE_WORKSPACE_KEY]);

  /**
   * Calculates the number of unread notifications.
   */
  const notificationsUnreadCount = useMemo(() => {
    if (!user || !activeWorkspaceKey || !Array.isArray(userNotifications)) return 0;

    return userNotifications
      .filter((notification) => {
        if (!notification || notification.isRead === true) return false;

        const notificationWorkspaceKey = String(notification?.workspaceKey || '').trim();
        return notificationWorkspaceKey === activeWorkspaceKey;
      })
      .length;
  }, [activeWorkspaceKey, user, userNotifications]);

  const activeSection = useMemo(() => {
    return inferSectionFromPath(location.pathname) || SectionType.HOME;
  }, [location.pathname]);

  const workspaceNavigationLocked = useMemo(() => {
    return isWorkspaceBillingNavigationLocked(billingRecord);
  }, [billingRecord]);

  const workspaceBillingNotice = useMemo(() => {
    return formatBillingLockNotice(billingRecord);
  }, [billingRecord]);

  /**
   * Menu Items
   */
  const menuItems = useMemo(() => ([
    {
      key: 'home',
      section: SectionType.HOME,
      iconKey: 'home',
      selectedIconKey: 'home_fill',
      title: 'Home',
    },
    {
      key: 'search',
      section: SectionType.SEARCH,
      iconKey: 'search',
      selectedIconKey: 'search',
      title: 'Search',
    },
    { key: 'add', type: 'add' },
    {
      key: 'notifications',
      section: SectionType.NOTIFICATIONS,
      iconKey: 'notifications',
      selectedIconKey: 'notifications',
      title: 'Notifications',
      count: notificationsUnreadCount,
    },
    {
      key: 'favorites',
      section: SectionType.FAVORITES,
      iconKey: 'bookmark',
      selectedIconKey: 'bookmark_fill',
      title: 'Favorites',
    },
  ]), [notificationsUnreadCount]);

  // ----- HANDLERS ----- //

  const handleLockedNavigationAttempt = useCallback(() => {
    setActiveDatabase(null);
    setCurrentNode(null);
    setCurrentSection(SectionType.HOME);

    if (location.pathname !== '/') {
      navigate('/', { replace: true });
    }
  }, [location.pathname, navigate, setActiveDatabase, setCurrentNode, setCurrentSection]);

  /**
   * Handles Home Click
   */
  const handleNavigate = useCallback((section) => {
    if (workspaceNavigationLocked && section !== SectionType.HOME) {
      handleLockedNavigationAttempt();
      return;
    }

    if (section !== SectionType.NODE) {
      setCurrentNode(null);
    }

    const targetPath = getSectionPath(section);

    setCurrentSection(section);
    navigate(targetPath);
  }, [handleLockedNavigationAttempt, navigate, setCurrentNode, setCurrentSection, workspaceNavigationLocked]);

  /**
   * Handles when new nodes are created (from NodeAdd).
   */
  /**
   * Handles click on the add node button.
   */
  const handleAddNodeClick = useCallback(() => {
    if (workspaceNavigationLocked) {
      handleLockedNavigationAttempt();
      return;
    }

    setAddModalOpen(true);
  }, [handleLockedNavigationAttempt, workspaceNavigationLocked]);

  const handleAttachmentSelect = useCallback((typeKey) => {
    if (workspaceNavigationLocked) {
      handleLockedNavigationAttempt();
      return;
    }

    setAddModalOpen(false);
    handleTopLevelActionSelect(typeKey);
  }, [handleLockedNavigationAttempt, handleTopLevelActionSelect, workspaceNavigationLocked]);

  const handleCloseAddModal = useCallback(() => {
    setAddModalOpen(false);
  }, []);

  // ----- RENDER ----- //

  return (
    <>
      <div className="mobile-menu-shell">

        {workspaceBillingNotice && (
          <div
            className="mobile-menu-billing-alert"
            style={{
              backgroundColor: workspaceNavigationLocked
                ? (theme.warning || theme.backgroundHover)
                : theme.backgroundHover,
              borderColor: workspaceNavigationLocked
                ? (theme.warning || theme.backgroundHover)
                : theme.backgroundHover,
              color: theme.foreground,
            }}
          >
            {workspaceBillingNotice}
          </div>
        )}

        {/* CONTAINER */}
        <div className="mobile-menu-container">

          {menuItems.map((item) => {

            if (item.type === 'add') {
              return (
                <MobileMenuAddButton
                  key={item.key}
                  enabled={!workspaceNavigationLocked}
                  onClick={handleAddNodeClick}
                />
              );
            }

            const isLockedTarget = workspaceNavigationLocked && item.section !== SectionType.HOME;
            const button = (
              <MobileMenuButton
                icon={item.icon}
                iconKey={item.iconKey}
                size={item.size}
                selectedIcon={item.selectedIcon}
                selectedIconKey={item.selectedIconKey}
                color={activeSection === item.section ? theme.foreground : theme.foregroundMuted}
                count={item.count ?? 0}
                enabled={!isLockedTarget}
                title={item.title}
                onClick={() => handleNavigate(item.section)}
                selected={activeSection === item.section}
              />
            );

            return React.cloneElement(button, { key: item.key });
          })}

        </div>
      </div>

      <input
        ref={uploadInputRef}
        type="file"
        accept={pendingUploadAccept}
        multiple={pendingUploadMultiple}
        style={{ display: 'none' }}
        onChange={handleUploadFileSelect}
      />

      <AddModal
        isOpen={addModalOpen}
        onClose={handleCloseAddModal}
        showFind={false}
        onTypeSelect={handleAttachmentSelect}
      />

      <CameraModal
        isOpen={cameraOpen}
        setOpen={setCameraOpen}
        contentType={cameraContentType}
        onContentCreated={handleCameraContentCreated}
      />

    </>
  );
};

export default MobileMenu;